import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../../components/ui/Modal'
import { Field } from '../../../components/ui/Field'
import { Input, Textarea, Select } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../../components/PermissionGate'
import { AttachmentList } from '../../../components/AttachmentList'
import { CopyFieldButton } from '../../../components/ui/CopyFieldButton'
import { DocumentLinkPicker } from '../../../components/DocumentLinkPicker'
import { tasksApi } from '../../../api/tasks.api'
import { labelsApi } from '../../../api/labels.api'
import { toast } from '../../../store/toastStore'
import { ApiError } from '../../../api/client'
import { PRIORITY_OPTIONS } from '../shared/PriorityBadge'
import { LabelPicker } from '../shared/LabelPicker'
import { AssigneePicker } from '../shared/AssigneePicker'
import { ProjectDocsPicker } from '../shared/ProjectDocsPicker'
import { AiToolToggle } from '../shared/AiToolToggle'
import { ProgressSlider } from '../shared/ProgressSlider'
import { ProgressReadonlyPill } from '../shared/ProgressReadonlyPill'
import { TaskComments } from './TaskComments'
import { TaskTimeTracking } from './TaskTimeTracking'
import { TaskHistoryList } from './TaskHistoryList'
import { TaskFormModal } from '../shared/TaskFormModal'
import type { AiTool, ProjectDocId, TaskPriority } from '../../../api/types'
import styles from './TaskDetailModal.module.css'

interface TaskDetailModalProps {
  taskId: string
  onClose: () => void
  onOpenTask?: (taskId: string) => void
}

export function TaskDetailModal({ taskId, onClose, onOpenTask }: TaskDetailModalProps) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [technicalScope, setTechnicalScope] = useState('')
  const [definitionOfDone, setDefinitionOfDone] = useState('')
  const [aiWorkInstruction, setAiWorkInstruction] = useState('')
  const [showSubtaskForm, setShowSubtaskForm] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [newChecklistText, setNewChecklistText] = useState('')

  const taskQuery = useQuery({ queryKey: ['tasks', taskId], queryFn: async () => (await tasksApi.get(taskId)).data })
  const task = taskQuery.data

  const statusesQuery = useQuery({
    queryKey: ['labels', 'statuses', task?.projectId],
    queryFn: async () => (await labelsApi.listStatuses(task!.projectId)).data,
    enabled: Boolean(task),
  })

  useEffect(() => {
    if (task) {
      setTitle(task.title)
      setDescription(task.description ?? '')
      setTechnicalScope(task.technicalScope ?? '')
      setDefinitionOfDone(task.definitionOfDone ?? '')
      setAiWorkInstruction(task.aiWorkInstruction ?? '')
    }
  }, [task])

  const invalidateTask = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks', taskId] })
    queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
  }

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof tasksApi.update>[1]) => tasksApi.update(taskId, input),
    onSuccess: invalidateTask,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const moveMutation = useMutation({
    mutationFn: (statusId: string) => tasksApi.move(taskId, statusId),
    onSuccess: invalidateTask,
  })

  const labelsMutation = useMutation({
    mutationFn: (labelIds: string[]) => tasksApi.setLabels(taskId, labelIds),
    onSuccess: invalidateTask,
  })

  const projectDocsMutation = useMutation({
    mutationFn: (relevantProjectDocs: ProjectDocId[]) => tasksApi.update(taskId, { relevantProjectDocs }),
    onSuccess: invalidateTask,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const documentsMutation = useMutation({
    mutationFn: (documentIds: string[]) => tasksApi.setDocuments(taskId, documentIds),
    onSuccess: invalidateTask,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const assigneesMutation = useMutation({
    mutationFn: (input: { userIds: string[]; roleIds: string[] }) => tasksApi.setAssignees(taskId, input.userIds, input.roleIds),
    onSuccess: invalidateTask,
  })

  const checklistAddMutation = useMutation({
    mutationFn: (text: string) => tasksApi.addChecklistItem(taskId, text),
    onSuccess: () => {
      setNewChecklistText('')
      invalidateTask()
    },
  })

  const checklistToggleMutation = useMutation({
    mutationFn: (input: { itemId: string; done: boolean }) => tasksApi.updateChecklistItem(taskId, input.itemId, { done: input.done }),
    onSuccess: invalidateTask,
  })

  const checklistDeleteMutation = useMutation({
    mutationFn: (itemId: string) => tasksApi.removeChecklistItem(taskId, itemId),
    onSuccess: invalidateTask,
  })

  const deleteMutation = useMutation({
    mutationFn: () => tasksApi.remove(taskId),
    onSuccess: () => {
      toast.success('Aufgabe gelöscht.')
      queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
      onClose()
    },
  })

  if (!task) {
    return (
      <Modal title="Aufgabe" onClose={onClose} size="lg">
        <span style={{ color: 'var(--text-muted)' }}>Lädt…</span>
      </Modal>
    )
  }

  const checklistDone = task.checklistItems.filter((i) => i.done).length
  const hasChecklist = task.checklistItems.length > 0

  return (
    <Modal
      title={`${task.project.name} · Aufgabe`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <PermissionGate anyOf={['tasks.delete']}>
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Löschen
            </Button>
          </PermissionGate>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </>
      }
    >
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => title.trim() && title !== task.title && updateMutation.mutate({ title })}
        style={{ fontSize: 'var(--text-xl)', fontWeight: 700, border: 'none', background: 'transparent', padding: 0 }}
      />

      <div className={styles.metaGrid}>
        <Field label="Status" htmlFor="td-status" hint="Alternative zum Verschieben per Drag & Drop im Board — auch touch-tauglich">
          <Select id="td-status" value={task.statusId} onChange={(e) => moveMutation.mutate(e.target.value)}>
            {(statusesQuery.data ?? [task.status]).map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Priorität" htmlFor="td-priority">
          <Select id="td-priority" value={task.priority} onChange={(e) => updateMutation.mutate({ priority: e.target.value as TaskPriority })}>
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fällig am" htmlFor="td-due">
          <Input
            id="td-due"
            type="date"
            value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
            onChange={(e) => updateMutation.mutate({ dueDate: e.target.value || null })}
          />
        </Field>
        <Field label="Fortschritt" htmlFor="td-progress" hint={hasChecklist ? 'Ergibt sich aus der Checkliste — dort abhaken statt hier eintragen.' : undefined}>
          {hasChecklist ? (
            <ProgressReadonlyPill value={task.progress} />
          ) : (
            <ProgressSlider id="td-progress" value={task.progress} onCommit={(progress) => updateMutation.mutate({ progress })} />
          )}
        </Field>
      </div>

      <div className={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
          <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>Beschreibung</div>
          <CopyFieldButton value={task.description} label="Beschreibung" />
        </div>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => description !== (task.description ?? '') && updateMutation.mutate({ description })}
          placeholder="Keine Beschreibung…"
          style={{ minHeight: 100 }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Labels</div>
        <LabelPicker
          projectId={task.projectId}
          selectedIds={task.labels.map((l) => l.labelId)}
          onToggle={(id) => {
            const current = task.labels.map((l) => l.labelId)
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
            labelsMutation.mutate(next)
          }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Verantwortliche</div>
        <AssigneePicker
          userIds={task.assignees.map((a) => a.userId)}
          roleIds={task.assigneeRoles.map((a) => a.roleId)}
          onToggleUser={(id) => {
            const current = task.assignees.map((a) => a.userId)
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
            assigneesMutation.mutate({ userIds: next, roleIds: task.assigneeRoles.map((a) => a.roleId) })
          }}
          onToggleRole={(id) => {
            const current = task.assigneeRoles.map((a) => a.roleId)
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
            assigneesMutation.mutate({ userIds: task.assignees.map((a) => a.userId), roleIds: next })
          }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Checkliste {task.checklistItems.length > 0 ? `(${checklistDone}/${task.checklistItems.length})` : ''}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {task.checklistItems.map((item) => (
            <div key={item.id} className={styles.checklistRow}>
              <input type="checkbox" checked={item.done} onChange={(e) => checklistToggleMutation.mutate({ itemId: item.id, done: e.target.checked })} />
              <span className={[styles.checklistText, item.done ? styles.checklistDone : ''].join(' ')}>{item.text}</span>
              <button
                onClick={() => checklistDeleteMutation.mutate(item.id)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Input
              placeholder="Neuer Punkt…"
              value={newChecklistText}
              onChange={(e) => setNewChecklistText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newChecklistText.trim() && checklistAddMutation.mutate(newChecklistText)}
            />
            <Button size="sm" disabled={!newChecklistText.trim()} onClick={() => checklistAddMutation.mutate(newChecklistText)}>
              +
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Unteraufgaben ({task.subtasks.length})</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 'var(--space-2)' }}>
          {task.subtasks.map((sub) => (
            <div key={sub.id} className={styles.subtaskRow} onClick={() => onOpenTask?.(sub.id)}>
              <span>{sub.title}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{sub.status.name}</span>
            </div>
          ))}
        </div>
        <Button size="sm" variant="secondary" onClick={() => setShowSubtaskForm(true)}>
          + Unteraufgabe
        </Button>
      </div>

      <div className={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
          <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>Technischer Scope</div>
          <CopyFieldButton value={task.technicalScope} label="Technischer Scope" />
        </div>
        <Textarea
          value={technicalScope}
          onChange={(e) => setTechnicalScope(e.target.value)}
          onBlur={() => technicalScope !== (task.technicalScope ?? '') && updateMutation.mutate({ technicalScope })}
          placeholder="Betroffene Resource, Domain, Services, Dependencies, APIs, DB-Bereiche, Security-Anforderungen…"
          style={{ minHeight: 100 }}
        />
      </div>

      <div className={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
          <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>Definition of Done</div>
          <CopyFieldButton value={task.definitionOfDone} label="Definition of Done" />
        </div>
        <Textarea
          value={definitionOfDone}
          onChange={(e) => setDefinitionOfDone(e.target.value)}
          onBlur={() => definitionOfDone !== (task.definitionOfDone ?? '') && updateMutation.mutate({ definitionOfDone })}
          placeholder="Wann gilt die Aufgabe als abgeschlossen?"
          style={{ minHeight: 100 }}
        />
      </div>

      <div className={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
          <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>KI-Arbeitsauftrag</div>
          <CopyFieldButton value={task.aiWorkInstruction} label="KI-Arbeitsauftrag" buttonLabel="KI-Prompt kopieren" />
        </div>
        <div style={{ marginBottom: 'var(--space-2)' }}>
          <AiToolToggle value={task.aiTool} onChange={(next: AiTool | null) => updateMutation.mutate({ aiTool: next })} />
        </div>
        <Textarea
          value={aiWorkInstruction}
          onChange={(e) => setAiWorkInstruction(e.target.value)}
          onBlur={() => aiWorkInstruction !== (task.aiWorkInstruction ?? '') && updateMutation.mutate({ aiWorkInstruction })}
          placeholder="Vollständiger Copy/Paste-Arbeitsauftrag für Claude Code oder Codex…"
          style={{ minHeight: 180 }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Relevante Projektdokumente</div>
        <ProjectDocsPicker
          variant="chips"
          selected={task.relevantProjectDocs ?? []}
          onToggle={(id) => {
            const current = task.relevantProjectDocs ?? []
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
            projectDocsMutation.mutate(next)
          }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Verknüpfte Dokumente</div>
        <DocumentLinkPicker
          projectId={task.projectId}
          linkedIds={task.documentLinks.map((l) => l.documentId)}
          onToggle={(id) => {
            const current = task.documentLinks.map((l) => l.documentId)
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
            documentsMutation.mutate(next)
          }}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Anhänge</div>
        <AttachmentList parentType="task" parentId={taskId} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Zeiterfassung</div>
        <TaskTimeTracking taskId={taskId} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Kommentare</div>
        <TaskComments taskId={taskId} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Historie</div>
        <TaskHistoryList taskId={taskId} />
      </div>

      {showSubtaskForm ? (
        <TaskFormModal
          projectId={task.projectId}
          parentTaskId={task.id}
          onClose={() => setShowSubtaskForm(false)}
          onCreated={() => invalidateTask()}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title="Aufgabe löschen"
          message={`"${task.title}" wirklich löschen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
